'use client'

import * as React from 'react'
import { Clock, LogIn, Zap, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAuth } from '@/components/auth'

interface RateLimitBannerProps {
  retryAfter: number // seconds until reset
  dayRemaining: number
  dayLimit: number
  onDismiss?: () => void
}

export function RateLimitBanner({
  retryAfter,
  dayRemaining,
  dayLimit,
  onDismiss,
}: RateLimitBannerProps) {
  const { user, signIn } = useAuth()
  const [secondsLeft, setSecondsLeft] = React.useState(retryAfter)
  const [mounted, setMounted] = React.useState(false)
  
  // Mount check for portal
  React.useEffect(() => {
    setMounted(true)
  }, [])
  
  // Countdown timer
  React.useEffect(() => {
    if (secondsLeft <= 0) {
      onDismiss?.()
      return
    }
    
    const timer = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer)
          onDismiss?.()
          return 0
        }
        return prev - 1
      })
    }, 1000)
    
    return () => clearInterval(timer)
  }, [secondsLeft, onDismiss])
  
  // Format seconds as M:SS
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }
  
  // Calculate progress percentage
  const progressPercent = dayLimit > 0 ? (dayRemaining / dayLimit) * 100 : 0
  
  // Calculate how many more searches auth users get (ratio)
  const authMultiplier = 25

  if (!mounted) return null

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 z-50 bg-black/50"
        onClick={onDismiss}
      />
      
      {/* Modal */}
      <div className="fixed z-50 inset-0 flex items-center justify-center p-4 pointer-events-none">
        <div 
          className={cn(
            'pointer-events-auto w-full max-w-md',
            'bg-background border border-border shadow-2xl elevation-lg rounded-2xl',
            'overflow-hidden'
          )}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header with gradient */}
          <div className="relative bg-gradient-to-br from-primary/10 via-primary/5 to-transparent p-6 pb-8">
            {/* Close button */}
            <button
              onClick={onDismiss}
              className="absolute top-3 right-3 p-2 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            
            {/* Icon */}
            <div className="w-14 h-14 rounded-full bg-primary/20 flex items-center justify-center mb-4">
              <Clock className="w-7 h-7 text-primary" />
            </div>
            
            {/* Title */}
            <h2 className="text-xl font-semibold text-foreground">
              Search Limit Reached
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              You've used your search quota. This helps keep Roostr free for everyone.
            </p>
          </div>
          
          {/* Content */}
          <div className="p-6 pt-0 -mt-2">
            {/* Countdown */}
            <div className="bg-muted/50 rounded-xl p-4 text-center">
              <p className="text-sm text-muted-foreground mb-2">
                Next search available in
              </p>
              <div className="font-mono text-4xl font-bold text-primary">
                {formatTime(secondsLeft)}
              </div>
            </div>
            
            {/* Daily quota */}
            <div className="mt-5">
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-muted-foreground">Today's quota</span>
                <span className="font-medium text-foreground">
                  {dayRemaining} / {dayLimit} left
                </span>
              </div>
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary rounded-full transition-all duration-300"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            </div>
            
            {/* Sign in prompt for unauthenticated users */}
            {!user && (
              <div className="mt-6 p-4 rounded-xl bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                    <Zap className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-foreground">
                      Get {authMultiplier}x more searches
                    </p>
                    <p className="text-sm text-muted-foreground mt-0.5">
                      Sign in for a much higher daily limit
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => signIn()}
                  className={cn(
                    'mt-4 w-full flex items-center justify-center gap-2',
                    'px-4 py-3 rounded-xl font-medium',
                    'bg-primary text-primary-foreground',
                    'hover:bg-primary/90 transition-colors'
                  )}
                >
                  <LogIn className="w-4 h-4" />
                  Sign in
                </button>
              </div>
            )}
            
            {/* Dismiss hint */}
            <p className="mt-5 text-center text-xs text-muted-foreground">
              Click anywhere outside to dismiss
            </p>
          </div>
        </div>
      </div>
    </>
  )
}
